// trainingEventService.js
import { apiClient, API_ENDPOINTS } from './api';
import { cacheService } from './cacheService';
import { appEvents, APP_EVENTS } from './appEvents';
import { dataEvents, EVENTS } from '../hooks/useDataEvents';

class TrainingEventService {
  _isLoadingEvents = false;
  _eventsPromise = null;
  _eventsTTL = 60 * 60 * 1000; // 60 минут для списка
  _eventDetailsTTL = 30 * 60 * 1000; // 30 минут для деталей

  // Получить все мероприятия (с возможностью фильтрации по статусу)
  async getAll(params = {}, forceRefresh = false) {
    const cacheKey = cacheService.getCacheKey('training_events_all', params);
    
    if (!forceRefresh) {
      const cached = cacheService.get(cacheKey, this._eventsTTL);
      if (cached) {
        return cached;
      }
    }
    
    if (this._isLoadingEvents && !forceRefresh) {
      return this._eventsPromise;
    }
    
    this._isLoadingEvents = true;
    this._eventsPromise = this._fetchAllEvents(params);
    
    try {
      const result = await this._eventsPromise;
      cacheService.set(cacheKey, result);
      return result;
    } finally {
      this._isLoadingEvents = false;
      this._eventsPromise = null;
    }
  }
  
  async _fetchAllEvents(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.status) queryParams.append('status', params.status);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      
      queryParams.append('fields', 'id,title,format,start_date,end_date,status,location,training_center,participants_count,max_participants,course_id,course_name');
      
      const queryString = queryParams.toString();
      const endpoint = queryString 
        ? `${API_ENDPOINTS.TRAINING_EVENTS}?${queryString}`
        : API_ENDPOINTS.TRAINING_EVENTS;
      
      const response = await apiClient.get(endpoint);
      
      let eventsArray = [];
      
      if (Array.isArray(response)) {
        eventsArray = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        eventsArray = response.data;
      } else if (response && response.events && Array.isArray(response.events)) {
        eventsArray = response.events;
      }
      
      return eventsArray.map(event => ({
        id: event.id,
        title: event.title || 'Без названия',
        course_id: event.course_id || (event.course ? event.course.id : null),
        course_name: event.course ? event.course.name : null,
        format: event.format || 'onsite',
        start_date: event.start_date,
        end_date: event.end_date,
        location: event.location || '',
        training_center: event.training_center || '',
        status: event.status || 'planned',
        participants_count: event.participants_count || 0,
        max_participants: event.max_participants || 0
      }));
    } catch (error) {
      console.error('❌ Ошибка загрузки мероприятий:', error);
      throw error;
    }
  }

  // Получить мероприятие по ID с полными данными
  async getById(id, forceRefresh = false) {
    const cacheKey = cacheService.getCacheKey(`training_event_${id}`);
    
    if (!forceRefresh) {
      const cached = cacheService.get(cacheKey, this._eventDetailsTTL);
      if (cached) {
        return cached;
      }
    }
    
    try {
      const response = await apiClient.get(API_ENDPOINTS.TRAINING_EVENT(id));
      
      const eventData = response.data || response;
      
      const normalizedData = {
        id: eventData.id,
        title: eventData.title || 'Без названия',
        course_id: eventData.course ? eventData.course.id : (eventData.course_id || null),
        course_name: eventData.course ? eventData.course.name : null,
        format: eventData.format || 'onsite',
        start_date: eventData.start_date,
        end_date: eventData.end_date,
        location: eventData.location || '',
        training_center: eventData.training_center || '',
        status: eventData.status || 'planned',
        cost: eventData.cost ? parseFloat(eventData.cost) : 0,
        max_participants: eventData.max_participants || 0,
        notes: eventData.notes || '',
        participants: eventData.participants || [],
        participants_count: eventData.participants_count || 0,
        created_at: eventData.created_at,
        updated_at: eventData.updated_at
      };
      
      cacheService.set(cacheKey, normalizedData);
      return normalizedData;
    } catch (error) {
      console.error(`❌ Ошибка загрузки мероприятия ${id}:`, error);
      throw error;
    }
  }

  // Создать мероприятие
  async create(data) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.TRAINING_EVENTS, data);
      this.invalidateCache();
      
      const createdEvent = response.data || response;
      appEvents.emit(APP_EVENTS.EVENT_CREATED, { 
        eventId: createdEvent.id,
        courseId: data.course_id,
        title: data.title,
        format: data.format,
        status: data.status,
        timestamp: new Date().toISOString()
      });
      dataEvents.emit(EVENTS.EVENT_CREATED, { eventId: createdEvent.id, ...data });
      
      return createdEvent;
    } catch (error) {
      console.error('Ошибка создания мероприятия:', error);
      throw error;
    }
  }

  // Обновить мероприятие
  async update(id, data) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.TRAINING_EVENT(id), data);
      cacheService.invalidate(`training_event_${id}`);
      this.invalidateCache();
      
      const updatedEvent = response.data || response;
      appEvents.emit(APP_EVENTS.EVENT_UPDATED, { 
        eventId: id,
        courseId: data.course_id,
        title: data.title,
        status: data.status,
        timestamp: new Date().toISOString()
      });
      dataEvents.emit(EVENTS.EVENT_UPDATED, { eventId: id, ...data });
      
      return updatedEvent;
    } catch (error) {
      console.error(`Ошибка обновления мероприятия ${id}:`, error);
      throw error;
    }
  }

  // Обновить статус мероприятия (отдельный метод для удобства)
  async updateStatus(id, status) {
    try {
      const currentEvent = await this.getById(id, true);
      
      const response = await apiClient.patch(API_ENDPOINTS.TRAINING_EVENT(id), { status });
      
      cacheService.invalidate(`training_event_${id}`);
      this.invalidateCache();
      
      appEvents.emit(APP_EVENTS.EVENT_UPDATED, { 
        eventId: id,
        oldStatus: currentEvent.status,
        newStatus: status,
        timestamp: new Date().toISOString()
      });
      dataEvents.emit(EVENTS.EVENT_UPDATED, { eventId: id, status });
      
      return response.data || response;
    } catch (error) {
      console.error(`Ошибка обновления статуса мероприятия ${id}:`, error);
      throw error;
    }
  }

  // Удалить мероприятие
  async delete(id) {
    try {
      await apiClient.delete(API_ENDPOINTS.TRAINING_EVENT(id));
      cacheService.invalidate(`training_event_${id}`);
      this.invalidateCache();
      
      appEvents.emit(APP_EVENTS.EVENT_DELETED, { 
        eventId: id,
        timestamp: new Date().toISOString()
      });
      dataEvents.emit(EVENTS.EVENT_DELETED, { eventId: id });
      
      return true;
    } catch (error) {
      console.error(`Ошибка удаления мероприятия ${id}:`, error);
      throw error;
    }
  }

  // Добавить участников к мероприятию
  async addParticipants(eventId, employeeIds, status = 'registered') {
    try {
      const data = {
        employee_ids: employeeIds,
        status: status
      };
      
      const response = await apiClient.post(
        API_ENDPOINTS.TRAINING_EVENT_PARTICIPANTS(eventId),
        data
      );
      
      cacheService.invalidate(`training_event_${eventId}`);
      this.invalidateCache();
      
      appEvents.emit(APP_EVENTS.ENROLLMENT_CREATED, { 
        eventId,
        employeeIds,
        status,
        count: employeeIds.length,
        timestamp: new Date().toISOString()
      });
      dataEvents.emit(EVENTS.ENROLLMENT_CREATED, { eventId, employeeIds, status });
      
      return response.data || response;
    } catch (error) {
      console.error(`Ошибка добавления участников к мероприятию ${eventId}:`, error);
      throw error;
    }
  }

  // Удалить участников из мероприятия
  async removeParticipants(eventId, participantIds) {
    try {
      const data = { participant_ids: participantIds };
      const response = await apiClient.post(
        API_ENDPOINTS.TRAINING_EVENT_PARTICIPANTS_REMOVE_BULK(eventId),
        data
      );
      
      cacheService.invalidate(`training_event_${eventId}`);
      this.invalidateCache();
      
      appEvents.emit(APP_EVENTS.ENROLLMENT_CANCELLED, { 
        eventId,
        participantIds,
        count: participantIds.length,
        timestamp: new Date().toISOString()
      });
      dataEvents.emit(EVENTS.ENROLLMENT_CANCELLED, { eventId, participantIds });
      
      return response.data || response;
    } catch (error) {
      console.error(`Ошибка удаления участников из мероприятия ${eventId}:`, error);
      throw error;
    }
  }

  // Получить участников мероприятия
  async getParticipants(eventId) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.TRAINING_EVENT_PARTICIPANTS(eventId));
      
      let participantsArray = [];
      
      if (Array.isArray(response)) {
        participantsArray = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        participantsArray = response.data;
      } else if (response && response.participants && Array.isArray(response.participants)) {
        participantsArray = response.participants;
      }
      
      return participantsArray;
    } catch (error) {
      console.error(`Ошибка загрузки участников мероприятия ${eventId}:`, error);
      throw error;
    }
  }

  // Получить мероприятия по курсу
  async getByCourse(courseId) {
    try {
      const endpoint = API_ENDPOINTS.TRAINING_EVENTS_BY_COURSE(courseId);
      const response = await apiClient.get(endpoint);
      
      let eventsArray = [];
      if (Array.isArray(response)) {
        eventsArray = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        eventsArray = response.data;
      } else if (response && response.events && Array.isArray(response.events)) {
        eventsArray = response.events;
      }
      
      return eventsArray;
    } catch (error) {
      console.error(`Ошибка загрузки мероприятий по курсу ${courseId}:`, error);
      throw error;
    }
  }

  // Получить календарь мероприятий
  async getCalendar(startDate, endDate) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const queryString = params.toString();
      const endpoint = queryString 
        ? `${API_ENDPOINTS.TRAINING_EVENTS_CALENDAR}?${queryString}`
        : API_ENDPOINTS.TRAINING_EVENTS_CALENDAR;
      
      const response = await apiClient.get(endpoint);
      
      let eventsArray = [];
      if (Array.isArray(response)) {
        eventsArray = response;
      } else if (response && response.data && Array.isArray(response.data)) {
        eventsArray = response.data;
      } else if (response && response.events && Array.isArray(response.events)) {
        eventsArray = response.events;
      }
      
      return eventsArray;
    } catch (error) {
      console.error('Ошибка загрузки календаря мероприятий:', error);
      throw error;
    }
  }

  // Инвалидация кеша
  invalidateCache() {
    cacheService.invalidate('training_events_all');
  }
  
  // Полная очистка кеша мероприятий
  clearCache() {
    cacheService.invalidate('training_event_');
    cacheService.invalidate('training_events_');
  }
}

export const trainingEventService = new TrainingEventService();